import { useMemo } from "react";
import { useCycles, type Cycle } from "@src/packages/cycles/hooks/useCycles";
import {
  useEmbryoReports,
  type EmbryoReport,
} from "@src/packages/cycles/hooks/useEmbryoReport";
import { requestedEmbryoTestingCyclesFixture } from "@src/packages/cycles/test-fixtures/embryoReportFixture";

/**
 * Cycles that can create embryos to test, from fresh eggs at a retrieval or
 * from frozen or donor eggs.
 */
const EMBRYO_CYCLE_TYPES: ReadonlySet<string> = new Set([
  "ivf",
  "icsi",
  "embryo_freezing",
]);

/** A cycle that can create embryos, as the page needs it to offer a report. */
export type EmbryoTestingCycle = Pick<Cycle, "id" | "cycleType" | "startDate">;

/**
 * Every embryo testing report the patient has, one per cycle, and the cycles
 * that can create embryos but have no report yet. The API has no patient-wide
 * list of reports, so this reads the report of each such cycle (plus the cycle
 * the page was opened for) through the same cache as `useEmbryoReport`. The
 * page orders the reports by batch, see `sortReportsByBatchDate`.
 */
export function useEmbryoTestingReports(openedCycleId: string): {
  reports: EmbryoReport[];
  cyclesWithoutReport: EmbryoTestingCycle[];
  isLoading: boolean;
} {
  const { data: cycles = [] } = useCycles();
  const fixtureCycles = requestedEmbryoTestingCyclesFixture();
  const embryoCycles = useMemo<EmbryoTestingCycle[]>(
    () =>
      fixtureCycles ??
      cycles.filter((cycle) => EMBRYO_CYCLE_TYPES.has(cycle.cycleType)),
    [cycles, fixtureCycles],
  );
  const cycleIds = useMemo(
    () => [
      openedCycleId,
      ...embryoCycles
        .map((cycle) => cycle.id)
        .filter((id) => id !== openedCycleId),
    ],
    [embryoCycles, openedCycleId],
  );
  const results = useEmbryoReports(cycleIds);

  const reports = results
    .map((result) => result.data)
    .filter((report): report is EmbryoReport => Boolean(report));
  // Only a settled answer of "no report" counts; a failed read is not one.
  const unreportedIds = new Set(
    cycleIds.filter((_, index) => {
      const result = results[index];
      return result?.isSuccess === true && !result.data;
    }),
  );
  const cyclesWithoutReport = embryoCycles
    .filter((cycle) => unreportedIds.has(cycle.id))
    .sort((a, b) => (b.startDate ?? "").localeCompare(a.startDate ?? ""));

  return {
    reports,
    cyclesWithoutReport,
    isLoading: results.some((result) => result.isLoading),
  };
}
